import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

const AIChat: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      role: 'assistant',
      content: 'Hej! Jag är din AI-assistent för stiftelseansökningar. Hur kan jag hjälpa dig idag?'
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsTyping(true);

    // Simulate AI response
    setTimeout(() => {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: generateAIResponse(userMessage)
      }]);
      setIsTyping(false);
    }, 1000);
  };

  const generateAIResponse = (userMessage: string): string => {
    const responses = [
      "Jag förstår ditt behov. Baserat på din beskrivning rekommenderar jag att fokusera på följande stiftelser...",
      "Bra fråga! För din situation skulle jag föreslå följande approach i din ansökan...",
      "Det finns flera stiftelser som matchar dina kriterier. Låt mig lista de mest relevanta...",
      "För att maximera dina chanser att få stöd, rekommenderar jag att du..."
    ];
    return responses[Math.floor(Math.random() * responses.length)];
  };

  return (
    <div className="h-[600px] flex flex-col">
      <div className="flex-grow overflow-y-auto mb-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex items-start gap-3 ${
              message.role === 'user' ? 'justify-end' : ''
            }`}
          >
            {message.role === 'assistant' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
                <Bot className="w-5 h-5 text-rose-600" />
              </div>
            )}
            <div
              className={`max-w-[80%] rounded-xl p-4 ${
                message.role === 'user'
                  ? 'bg-rose-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {message.content}
            </div>
            {message.role === 'user' && (
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-600 flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
            )}
          </div>
        ))}
        {isTyping && (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center">
              <Bot className="w-5 h-5 text-rose-600" />
            </div>
            <div className="bg-gray-100 rounded-xl p-4">
              <div className="flex space-x-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-100"></div>
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce delay-200"></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <form onSubmit={handleSubmit} className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Skriv ditt meddelande här..."
          className="flex-grow rounded-lg border-gray-300 shadow-sm focus:border-rose-500 focus:ring-rose-500"
        />
        <button
          type="submit"
          className="px-4 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 
                   transition-colors flex items-center gap-2"
        >
          <Send className="w-5 h-5" />
          <span>Skicka</span>
        </button>
      </form>
    </div>
  );
};

export default AIChat;